import type { SequencedCategorySection, SequencedEntity, SequencedItem } from '../types/sortable';

export function sortConsideringExistence(
  element1: number,
  element2: number,
  existenceChecker: (element: number | undefined) => boolean,
) {
  const element1Exists = existenceChecker(element1);
  const element2Exists = existenceChecker(element2);
  if (element1Exists && element2Exists) {
    return element1 - element2;
  }
  return element1Exists ? -1 : 1;
}

function getOfferIDSupportingInstitutionRoundup(marketplaceEntity: SequencedEntity) {
  // For institution Roundups we need to use offerForCta data because
  // marketplaceEntity refers to the institution, not the offer
  if ('offerForCta' in marketplaceEntity && marketplaceEntity.offerForCta) {
    return marketplaceEntity.offerForCta.id;
  }
  return marketplaceEntity.id;
}

export function getSortedSummaryTableRows<TRow extends SequencedItem>(
  rows: TRow[],
  sequence: string[],
): TRow[] {
  const rowsClone = [...rows];

  const orderMapping: Record<string, number> = {};

  sequence.forEach((id, index) => (orderMapping[id] = index));

  return rowsClone.sort((a, b) => {
    const offerAID = getOfferIDSupportingInstitutionRoundup(a.marketplaceEntity);
    const offerBID = getOfferIDSupportingInstitutionRoundup(b.marketplaceEntity);
    return sortConsideringExistence(
      orderMapping[offerAID],
      orderMapping[offerBID],
      element => element !== undefined,
    );
  });
}

export function getSortedProductCardSections<
  TCard extends SequencedItem,
  TSection extends SequencedCategorySection<TCard>,
>(
  sections: { categorySections: TSection[] },
  sequence: string[],
): { categorySections: TSection[] } {
  const orderMapping: Record<string, number> = {};
  sequence.forEach((id, index) => (orderMapping[id] = index));

  const getSortIndex = (id: string): number => orderMapping[id] ?? Infinity;

  const sortedSections = sections.categorySections.map(section => ({
    ...section,
    productCards: [...section.productCards].sort((a, b) => {
      const offerAID = getOfferIDSupportingInstitutionRoundup(a.marketplaceEntity);
      const offerBID = getOfferIDSupportingInstitutionRoundup(b.marketplaceEntity);

      return getSortIndex(offerAID) - getSortIndex(offerBID);
    }),
  }));

  sortedSections.sort((a, b) => {
    const offerAID = getOfferIDSupportingInstitutionRoundup(a.productCards[0].marketplaceEntity);
    const offerBID = getOfferIDSupportingInstitutionRoundup(b.productCards[0].marketplaceEntity);

    const aIndex = getSortIndex(offerAID);
    const bIndex = getSortIndex(offerBID);

    return aIndex - bIndex;
  });

  return {
    categorySections: sortedSections,
  };
}

export function getSortedOfferEntites<TCard extends SequencedItem>({
  offerEntities,
  sequence,
}: {
  offerEntities: TCard[];
  sequence: string[];
}): TCard[] {
  const offerToPositionMap = sequence.reduce(
    (acc, id, idx) => {
      return {
        ...acc,
        [id]: idx,
      };
    },
    {} as Record<string, number>,
  );

  const getSortingIndex = (id: string) => offerToPositionMap[id] ?? Infinity;

  return offerEntities.toSorted((a, b) => {
    return getSortingIndex(a.marketplaceEntity.id) - getSortingIndex(b.marketplaceEntity.id);
  });
}
